// convert from hacker to mentor

import { useEffect, useState } from 'react';
import {
  CalendarDays,
  Users,
  User,
  Utensils,
  Code,
} from 'lucide-react';

import ScanBadgeButton from '../../../components/scan-badge-button/scan-badge-button';

import { fetchUserRecord } from '../../../utils/mongoDataOperations';
import { formatDateTime } from '../../../utils/helpers';
import { userData } from '../../../assets/organizerList';

const Info = () => {
  const [scannedReturnedID, setScannedReturnedID] = useState(null);
  const [scannedUserRecord, setScannedUserRecord] = useState(null);

  const getOrganizerName = (email) => {
    const organizer = userData.users.find((user) => user.email === email);
    return organizer ? organizer.name : email;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (scannedReturnedID) {
        try {
          const response = await fetchUserRecord(scannedReturnedID);
          setScannedUserRecord(response);
        } catch (error) {
          console.error('Error fetching user record:', error);
        }
      }
    };
    fetchData();
  }, [scannedReturnedID]);

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className='flex items-center mb-4 space-x-3'>
      <div className='bg-gray-100 flex h-8 items-center justify-center rounded-full w-8'>
        <Icon className='h-4 text-gray-600 w-4' />
      </div>
      <div>
        <p className='text-gray-500 text-sm'>{label}</p>
        <p className='font-medium'>{value || '...'}</p>
      </div>
    </div>
  );

  const Card = ({ title, children, className }) => (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      {title && <h2 className='font-semibold mb-4 text-xl'>{title}</h2>}
      {children}
    </div>
  );

  const Badge = ({ children, variant = 'default' }) => {
    const variants = {
      default: 'bg-gray-100 text-gray-800',
      success: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800',
      blue: 'bg-blue-100 text-blue-800',
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${variants[variant]}`}>
        {children}
      </span>
    );
  };

  const formatMealCode = (code) => {
    const [day, meal] = code.split('-');
    return `${day.charAt(0).toUpperCase() + day.slice(1)} ${
      meal.charAt(0).toUpperCase() + meal.slice(1)
    }`;
  };

  return (
    <div className='max-w-4xl mx-auto p-6 space-y-6'>
      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center space-x-4'>
          <h1 className='font-bold text-3xl'>Mentor Info</h1>
        </div>
        <div className='flex items-center space-x-3'>
          <button
            onClick={() => {
              setScannedUserRecord(null);
              setScannedReturnedID(null);
            }}
            className='border border-gray-300 hover:bg-gray-50 inline-flex items-center px-4 py-2 rounded-lg text-gray-700 transition-colors'>
            Clear
          </button>
          <ScanBadgeButton
            scannedReturnedID={scannedReturnedID}
            setScannedReturnedID={setScannedReturnedID}
          />
        </div>
      </div>

      <div className='gap-6 grid grid-cols-1 md:grid-cols-2'>
        <Card title='Personal Info'>
          <InfoItem
            icon={Code}
            label='TreeHacks ID'
            value={scannedReturnedID}
          />
          <InfoItem
            icon={User}
            label='Full Name'
            value={scannedUserRecord?.application_info?.full_name}
          />
        </Card>

        <Card title='Event Info'>
          <InfoItem
            icon={User}
            label='T-Shirt Size'
            value={scannedUserRecord?.check_in_info?.tshirtSize}
          />
          <InfoItem
            icon={Users}
            label='Check-in Status'
            value={
              <Badge
                variant={
                  scannedUserRecord?.check_in_info?.checkedIn
                    ? 'success'
                    : 'default'
                }>
                {scannedUserRecord?.check_in_info?.checkedIn
                  ? 'Checked In'
                  : 'Not Checked In'}
              </Badge>
            }
          />
          {scannedUserRecord?.check_in_info?.checkedIn && (
            <>
              <InfoItem
                icon={CalendarDays}
                label='Checked In At'
                value={formatDateTime(
                  scannedUserRecord?.check_in_info?.checkedInAt
                )}
              />
              <InfoItem
                icon={User}
                label='Checked In By'
                value={
                  scannedUserRecord?.check_in_info?.checkedInBy
                    ? getOrganizerName(
                        scannedUserRecord.check_in_info.checkedInBy
                      )
                    : '...'
                }
              />
            </>
          )}
        </Card>

        <Card
          title='Meal Info'
          className='md:col-span-2'>
          <div className='space-y-6'>
            <InfoItem
              icon={Utensils}
              label='Dietary Restrictions'
              value={
                scannedUserRecord?.meal_info?.dietaryRestrictions || 'None'
              }
            />
            <div>
              <h3 className='font-medium mb-3'>Claimed Meals</h3>
              <div className='gap-4 grid grid-cols-1 md:grid-cols-3'>
                {scannedUserRecord?.meal_info?.usedMeals?.length > 0 ? (
                  scannedUserRecord.meal_info.usedMeals.map((meal, index) => (
                    <div
                      key={index}
                      className='bg-gray-50 flex items-center justify-between p-3 rounded-lg'>
                      <span className='text-sm'>{formatMealCode(meal)}</span>
                      <Badge variant='success'>Claimed</Badge>
                    </div>
                  ))
                ) : (
                  <p className='text-gray-500 text-sm'>No meals claimed yet</p>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Info;
